import React, { useState } from 'react';
import { Form, Button, Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { apiService, LoginCredentials } from '../services/api';

interface LoginProps {
  onLogin: (user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    nick_usuario: '',
    pass_usuario: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!credentials.nick_usuario.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo requerido',
        text: 'Debe ingresar el nombre de usuario',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#0066CC'
      });
      return false;
    }
    if (!credentials.pass_usuario.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo requerido',
        text: 'Debe ingresar la contraseña',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#0066CC'
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiService.login(credentials);
      
      if (response.estado === 1) {
        // Guardar datos de sesión
        localStorage.setItem('user', JSON.stringify(response.usuario));
        localStorage.setItem('authToken', 'temp-token'); // Temporal hasta implementar JWT
        
        Swal.fire({
          icon: 'success',
          title: '¡Bienvenido!',
          text: `Hola ${response.usuario.nombre_usuario}`,
          confirmButtonText: 'Continuar',
          confirmButtonColor: '#28A745',
          timer: 2000,
          timerProgressBar: true
        }).then(() => {
          onLogin(response.usuario);
          navigate('/dashboard');
        });
      } else {
        setError('Credenciales inválidas');
        Swal.fire({
          icon: 'error',
          title: 'Error de autenticación',
          text: 'Usuario o contraseña incorrectos',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#DC3545'
        });
      }
    } catch (error: any) {
      console.error('Error en login:', error);
      setError('Error al conectar con el servidor');
      Swal.fire({
        icon: 'error',
        title: 'Error de conexión',
        text: 'No se pudo conectar con el servidor. Verifique su conexión.',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#DC3545'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-gradient-primary">
      <Row className="justify-content-center w-100">
        <Col md={6} lg={4} xl={3}>
          <Card className="shadow-lg border-0">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <div className="mb-3">
                  <i className="fas fa-store fa-3x text-primary"></i>
                </div>
                <h2 className="text-primary fw-bold mb-1">SISTEMA POS</h2>
                <p className="text-muted">Inicio de Sesión</p>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Usuario</Form.Label>
                  <Form.Control
                    type="text"
                    name="nick_usuario"
                    value={credentials.nick_usuario}
                    onChange={handleChange}
                    placeholder="Ingrese su usuario"
                    className="form-control-lg"
                    disabled={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    name="pass_usuario"
                    value={credentials.pass_usuario}
                    onChange={handleChange}
                    placeholder="Ingrese su contraseña"
                    className="form-control-lg"
                    disabled={loading}
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="fw-semibold"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Iniciando sesión...
                      </>
                    ) : (
                      'Iniciar Sesión'
                    )}
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-4">
                <small className="text-muted">
                  ¿Olvidó su contraseña? Contacte al administrador
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #0066CC 0%, #004499 100%);
        }
        .card {
          border-radius: 15px;
        }
        .form-control-lg {
          border-radius: 10px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }
        .form-control-lg:focus {
          border-color: #0066CC;
          box-shadow: 0 0 0 0.2rem rgba(0, 102, 204, 0.25);
        }
        .btn-lg {
          border-radius: 10px;
          font-weight: 600;
          padding: 12px 24px;
        }
      `}</style>
    </Container>
  );
};

export default Login;
